import { useEffect, useState } from "react";
import { Field, useFormikContext } from "formik";
import classNames from "classnames";
import { useTranslations } from "next-intl";

import {
  CommonFormLayout,
  DetailedRadioButton,
  Error,
  FormRow,
  Input,
  Modal,
  NumberStepper,
  SearchInput,
  Textarea,
} from "@/components/common";

import { useGetShipmentTypesRequest } from "@/api/useGetShipmentTypesRequest";
import { Category } from "../../../get-parcels-table/components";
import { TParcelItemsGE } from "@/types/parcel.types";
import { ModalType } from "@/enums";

interface ParcelItemModalProps {
  modalOpen: boolean;
  closeModal: () => void;
  modalType: ModalType | null;
}

const ParcelItemModalGE = ({
  modalOpen,
  closeModal,
  modalType,
}: ParcelItemModalProps) => {
  const t = useTranslations();

  const [searchValue, setSearchValue] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    values,
    setFieldValue,
    validateForm,
    errors,
    setErrors,
    setFieldTouched,
    getFieldMeta,
  } = useFormikContext<TParcelItemsGE>();
  const {
    products,
    index,
    productCategoryId,
    productPriceUsd,
    productAmount,
    productDescription,
  } = values;

  const { data: productCategories } = useGetShipmentTypesRequest();
  const productCategoriesData = productCategories?.data?.data || [];

  useEffect(() => {
    if (modalOpen && modalType === ModalType.ADD) {
      setFieldValue("index", undefined);
      setFieldValue("productCategoryId", undefined);
      setFieldValue("productPriceUsd", null);
      setFieldValue("productAmount", null);
      setFieldValue("productDescription", "");
      setFieldTouched("productCategoryId", false);
      setFieldTouched("productPriceUsd", false);
      setFieldTouched("productAmount", false);
      setFieldTouched("productDescription", false);
    }
  }, [modalOpen]);

  const handleMainButtonClick = async () => {
    setIsSubmitted(true);
    try {
      const validationResponse = await validateForm();
      if (validationResponse) {
        if (Object.keys(validationResponse).length === 0) {
          if (products?.length > 0) {
            if (typeof index === "number") {
              const updatedProducts = [...products];
              updatedProducts[index] = {
                ...updatedProducts[index],
                productCategoryId,
                productPriceUsd: productPriceUsd?.toString(),
                productAmount,
                productDescription,
              };
              setFieldValue("products", updatedProducts);
              closeModal();
              return;
            }
            setFieldValue("products", [
              ...products,
              {
                productCategoryId,
                productPriceUsd,
                productAmount,
                productDescription,
              },
            ]);
          } else {
            setFieldValue("products", [
              {
                productCategoryId: productCategoryId,
                productPriceUsd: productPriceUsd,
                productAmount: productAmount,
                productDescription: productDescription,
              },
            ]);
          }
          setErrors({});
          closeModal();
        } else {
          console.error(validationResponse);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      title="add_product"
      showActions
      isOpen={modalOpen}
      closeModal={closeModal}
      headerPaddings="px-[3.125rem]"
      paddings="!px-0"
      titleClassNames="text-[2rem] font-semibold"
    >
      <CommonFormLayout
        mainButton={t(index ? "save" : "add")}
        secondaryButtonText={t("cancel")}
        secondaryOnClick={closeModal}
        noSubmit
        stickyButtons
        mainButtonOnClick={handleMainButtonClick}
      >
        <div className="px-[3.125rem]">
          <FormRow className="w-full" rowLabel="product_details">
            <div className={classNames("flex w-full gap-[15px]")}>
              <Field name={`productPriceUsd`}>
                {({ field, meta }) => (
                  <Input
                    label="product_price"
                    value={productPriceUsd}
                    error={
                      isSubmitted ? meta.error : meta.touched && meta.error
                    }
                    currency="$"
                    {...field}
                    onChange={(e) => {
                      const inputValue = e.target.value;

                      if (!/^\d*\.?\d*$/.test(inputValue)) {
                        return;
                      } else {
                        if (inputValue.split(".")[1]?.length > 2) {
                          return;
                        }
                        setFieldValue("productPriceUsd", inputValue);
                      }
                    }}
                  />
                )}
              </Field>
              <Field name={`productAmount`}>
                {({ field, meta }) => (
                  <NumberStepper
                    label="quantity"
                    value={productAmount}
                    {...field}
                    onNumberChange={(value: string) => {
                      setFieldValue("productAmount", parseFloat(value));
                    }}
                    onChange={(e) => {
                      const inputValue = (e.target as HTMLTextAreaElement)
                        .value;
                      if (/[^0-9]/.test(inputValue)) {
                        return;
                      } else {
                        field.onChange(e);
                      }
                    }}
                    error={
                      isSubmitted ? meta.error : meta.touched && meta.error
                    }
                  />
                )}
              </Field>
            </div>
          </FormRow>
          <FormRow className="w-full" rowLabel="choose_category">
            <div className="flex flex-col justify-between gap-[10px]">
              <SearchInput
                label={t("search_category")}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue((e.target as HTMLTextAreaElement).value);
                }}
              />
              {productCategoriesData
                ?.filter((cat) =>
                  cat.shipmentTypeName
                    ?.toLocaleLowerCase()
                    .includes(searchValue?.toLocaleLowerCase()),
                )
                ?.map((cat, i) => {
                  return (
                    <DetailedRadioButton
                      key={i}
                      id={i.toString()}
                      name="shipmentType"
                      label={cat.shipmentTypeName}
                      checked={productCategoryId?.id === cat.shipmentTypeId}
                      onChange={(e) => {
                        (e.target as HTMLInputElement).checked &&
                          setFieldValue("productCategoryId", {
                            id: cat.shipmentTypeId,
                            mainText: cat.shipmentTypeName,
                          });
                      }}
                      icon={<Category category={cat.shipmentTypeId} />}
                      error={
                        isSubmitted
                          ? getFieldMeta("productCategoryId").error
                          : getFieldMeta("productCategoryId").touched &&
                            getFieldMeta("productCategoryId").error
                      }
                      handleTouch={() => setFieldTouched("productCategoryId")}
                      translation={false}
                    />
                  );
                })}
            </div>
            {isSubmitted && errors?.productCategoryId && (
              <Error text={errors?.productCategoryId?.id?.toString()} />
            )}
          </FormRow>
          <Field name={`productDescription`}>
            {({ field, meta }) => (
              <FormRow className="w-full">
                <Textarea
                  label="description"
                  value={productDescription}
                  resize="resize-y"
                  className="!rounded-[2.25rem] !px-[2rem] !text-base font-normal"
                  labelClassName="!left-[2rem]"
                  error={isSubmitted ? meta.error : meta.touched && meta.error}
                  {...field}
                  onChange={(e) => {
                    setFieldValue(`productDescription`, e.target.value);
                  }}
                />
              </FormRow>
            )}
          </Field>
        </div>
      </CommonFormLayout>
    </Modal>
  );
};

export default ParcelItemModalGE;
